import {
    Box,
    IconButton,
    SxProps,
    Theme,
    Typography,
    styled,
} from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import {
    DateCalendar,
    PickersCalendarHeaderProps,
} from '@mui/x-date-pickers/DateCalendar';
import moment, { Moment } from 'moment/moment';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useObserveElementRect } from '@/hooks/useObserveElementRect';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import './DateRangePicker.scss';
import { IcoBetRecordChevron } from './svg';
const CustomCalendarHeaderRoot = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    padding: '8px 16px',
    alignItems: 'center',
});
const CustomCalendarHeader = (props: PickersCalendarHeaderProps<Moment>) => {
    const { t } = useTranslation();

    const { currentMonth, onMonthChange } = props;
    const d = currentMonth.clone();
    const selectNextMonth = () => onMonthChange(d.add({ months: 1 }), 'left');
    const selectPreviousMonth = () =>
        onMonthChange(d.add({ months: -1 }), 'right');
    const [monthLabel, setMonthLabel] = useState('');

    useEffect(() => {
        setMonthLabel(
            t(`betrecord.month.${currentMonth.month()}`) +
                ' ' +
                currentMonth.year()
        );
    }, [currentMonth]);

    return (
        <CustomCalendarHeaderRoot className="custom-calendar-header-root">
            <Stack spacing={1} direction="row">
                <IconButton
                    onClick={selectPreviousMonth}
                    title="Previous month"
                >
                    <ChevronLeft />
                </IconButton>
            </Stack>
            <Typography variant="body2" color={'#bf9a5d'}>
                {monthLabel}
            </Typography>
            <Stack spacing={1} direction="row">
                <IconButton onClick={selectNextMonth} title="Next month">
                    <ChevronRight />
                </IconButton>
            </Stack>
        </CustomCalendarHeaderRoot>
    );
};
interface CustomPickerDayProps extends PickersDayProps<Moment> {
    isSelected: boolean;
    isHovered: boolean;
    isFirstSelected: boolean;
    isLastSelected: boolean;
}

const CustomPickersDay = styled(PickersDay, {
    shouldForwardProp: prop =>
        prop !== 'isSelected' &&
        prop !== 'isHovered' &&
        prop !== 'isFirstSelected' &&
        prop !== 'isLastSelected',
})<CustomPickerDayProps>(({ /*theme,*/ isFirstSelected, isLastSelected }) => ({
    borderRadius: 0,
    ...(isFirstSelected && {
        borderRadius: '50%',
        border: '2px solid #BF9A5D',
    }),
    ...(isLastSelected && {
        borderRadius: '50%',
        border: '2px solid #BF9A5D',
    }),
})) as React.ComponentType<CustomPickerDayProps>;

type DayProps = PickersDayProps<Moment> & {
    selectedDay?: Array<Moment>;
    hoveredDay?: Moment;
};
const Day = (props: DayProps) => {
    const { day, selectedDay, hoveredDay, ...other } = props;
    let isSelected = false;
    let isHovered = false;
    let isFirstSelected = false;
    let isLastSelected = false;
    if (selectedDay) {
        isSelected = selectedDay.some(d => d.isSame(day, 'date'));
        // assume selected moment array is sorted
        isFirstSelected = day.isSame(selectedDay[0], 'date');
        isLastSelected = day.isSame(
            selectedDay[selectedDay.length - 1],
            'date'
        );
    }
    if (hoveredDay) {
        isHovered = day.isSame(hoveredDay, 'date');
    }
    /**
     * the container props is for the first and last selected day's background
     * if doesn't add this, the first selected day will missing top-right and bottom-right background...
     */
    const containerProps: SxProps<Theme> = {
        background: isSelected ? '#3E3323' : 'transparent',
    };
    if (isFirstSelected) {
        containerProps.borderTopLeftRadius = '50%';
        containerProps.borderBottomLeftRadius = '50%';
    }
    if (isLastSelected) {
        containerProps.borderTopRightRadius = '50%';
        containerProps.borderBottomRightRadius = '50%';
    }
    return (
        <Box sx={containerProps}>
            <CustomPickersDay
                {...other}
                className="custom-picker-day"
                disableMargin
                day={day}
                selected={false}
                isSelected={isSelected}
                isHovered={isHovered}
                isFirstSelected={isFirstSelected}
                isLastSelected={isLastSelected}
            />
        </Box>
    );
};

type DateRangeDate = Moment | undefined;
type DateRangePickerGetProps = {
    start: DateRangeDate;
    end: DateRangeDate;
    expanded: boolean;
};
type DateRangePickerSetProps = {
    setStart: Dispatch<SetStateAction<DateRangeDate>>;
    setEnd: Dispatch<SetStateAction<DateRangeDate>>;
    setExpanded: Dispatch<SetStateAction<boolean>>;
    handleSubmit?: (
        startDate: Moment,
        endDate: Moment,
        isRecent: boolean,
        isSummaryExpand: boolean
    ) => void;
    playButtonAudio: () => void;
};
type DateRangePickerOwnProps = {
    showPreset: boolean;
    showRecent?: boolean;
    gaPrefix?: string;
};
type DateRangePickerProps = DateRangePickerOwnProps &
    DateRangePickerGetProps &
    DateRangePickerSetProps;

const getSAToday = () => {
    const now = moment();
    let l = moment().utcOffset('+0800').set({ hour: 12, minute: 0, second: 0 });
    const isAfter = now.isAfter(l);
    if (isAfter) l = l.add(1, 'day');
    return l;
};

export const DateRangePicker = (props: DateRangePickerProps) => {
    const {
        start,
        end,
        expanded,
        setStart,
        setEnd,
        setExpanded,
        showPreset,
        showRecent = false,
        handleSubmit,
        playButtonAudio,
        gaPrefix = '',
    } = props;
    const { t } = useTranslation();
    const [selectedButton, setSelectedButton] = useState<number>(0);
    const [hoveredDay, setHoveredDay] = useState<Moment>();
    const [limit, setLimit] = useState<Moment>();
    const [selectedDay, setSelectedDay] = useState<Array<Moment>>([]);
    const { ref: calendarRef, rect: calendarRect } =
        useObserveElementRect<HTMLDivElement>();
    const [buttonContainerWidth, setButtonContainerWidth] = useState('100%');
    useEffect(() => {
        if (calendarRect !== undefined) {
            const w = calendarRect.width;
            setButtonContainerWidth(`${w}px`);
        }
    }, [calendarRect]);
    useEffect(() => {
        const l = getSAToday();

        console.log('date::picker::limit', l);
        setLimit(l);

        //set default
        const now = getSAToday(); //moment().add(1, 'day');
        const last = showRecent
            ? moment(now).subtract(3, 'months')
            : moment(now).subtract(7, 'day');
        setStart(last);
        setEnd(now);
        handleSubmit?.(last, now, showRecent, true);
    }, []);

    useEffect(() => {
        const s = new Array<Moment>();
        if (start && end) {
            for (
                let m = moment(start);
                m.isSameOrBefore(end, 'date');
                m = m.add(1, 'day')
            ) {
                s.push(moment(m));
            }
        } else if (start) {
            s.push(start);
        }

        setSelectedDay(s);
    }, [start, end]);

    const reset = () => {
        setStart(undefined);
        setEnd(undefined);
        setSelectedDay([]);
    };
    const resetToday = () => {
        const now = getSAToday();
        const last = moment(now).subtract(1, 'day');
        setStart(last);
        setEnd(now);
        setSelectedDay([last, now]);
    };
    const handleChangeDate = (d: Moment) => {
        if (start && end) {
            reset();
            setStart(d);
        } else if (start) {
            setEnd(d);
            handleSubmit?.(start, d, false, false);
        } else {
            setStart(d);
        }
    };
    const [presetRangeButtonCN, setPresetRangeButtonCN] = useState<string>(
        'preset-range-button'
    );
    useEffect(() => {
        setPresetRangeButtonCN(
            showRecent
                ? 'preset-with-recent-range-button'
                : 'preset-range-button'
        );
    }, [showRecent]);

    return (
        <>
            <Stack id="date-range-picker">
                <Accordion expanded={expanded}>
                    <AccordionSummary className="date-range-picker">
                        <Grid className="date-range-container" item container>
                            <div
                                id={`${gaPrefix}_to-date-from-date`}
                                className="date-range-list"
                                onClick={() => {
                                    setSelectedButton(-1);
                                    setExpanded(!expanded);
                                    playButtonAudio();
                                }}
                            >
                                <DateRangeButton
                                    time={start}
                                    label="12:00:00"
                                />
                                <IcoBetRecordChevron className="date-range-chevron" />
                                <DateRangeButton time={end} label="11:59:59" />
                            </div>
                            {showPreset && (
                                <Grid
                                    item
                                    container
                                    className="days-button-list"
                                >
                                    {showRecent && (
                                        <PresetRangeButton
                                            gaKey={`${gaPrefix}_recent`}
                                            cn={presetRangeButtonCN}
                                            Last={0}
                                            setStart={setStart}
                                            setEnd={setEnd}
                                            setExpanded={setExpanded}
                                            handleSubmit={handleSubmit}
                                            playButtonAudio={playButtonAudio}
                                            selected={selectedButton}
                                            setSelectedButton={
                                                setSelectedButton
                                            }
                                        />
                                    )}
                                    <PresetRangeButton
                                        gaKey={`${gaPrefix}_today`}
                                        cn={presetRangeButtonCN}
                                        Last={1}
                                        setStart={setStart}
                                        setEnd={setEnd}
                                        setExpanded={setExpanded}
                                        handleSubmit={handleSubmit}
                                        playButtonAudio={playButtonAudio}
                                        selected={selectedButton}
                                        setSelectedButton={setSelectedButton}
                                    />
                                    <PresetRangeButton
                                        gaKey={`${gaPrefix}_last7`}
                                        cn={presetRangeButtonCN}
                                        Last={7}
                                        setStart={setStart}
                                        setEnd={setEnd}
                                        setExpanded={setExpanded}
                                        handleSubmit={handleSubmit}
                                        playButtonAudio={playButtonAudio}
                                        selected={selectedButton}
                                        setSelectedButton={setSelectedButton}
                                    />
                                    <PresetRangeButton
                                        gaKey={`${gaPrefix}_last14`}
                                        cn={presetRangeButtonCN}
                                        Last={14}
                                        setStart={setStart}
                                        setEnd={setEnd}
                                        setExpanded={setExpanded}
                                        handleSubmit={handleSubmit}
                                        playButtonAudio={playButtonAudio}
                                        selected={selectedButton}
                                        setSelectedButton={setSelectedButton}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </AccordionSummary>
                    {/* {expanded && <ReportDivider/>} */}
                    <AccordionDetails className="date-calender-container">
                        <DateCalendar
                            className="date-calender mx-auto"
                            ref={calendarRef}
                            views={['day']}
                            maxDate={limit}
                            onChange={value => {
                                const m = moment(value);
                                handleChangeDate(m);
                            }}
                            slots={{
                                day: Day,
                                calendarHeader: CustomCalendarHeader,
                            }}
                            slotProps={{
                                day: ownerState => ({
                                    selectedDay,
                                    hoveredDay,
                                    onPointerEnter: () =>
                                        setHoveredDay(ownerState.day),
                                    onPointerLeave: () =>
                                        setHoveredDay(undefined),
                                }),
                            }}
                            showDaysOutsideCurrentMonth
                        />
                        {/* <ReportDivider/> */}
                        <Grid
                            container
                            className="calender-button-container mx-auto"
                            width={buttonContainerWidth}
                            marginTop={2}
                        >
                            <Button
                                id={`${gaPrefix}_reset`}
                                onClick={() => {
                                    resetToday();
                                    playButtonAudio();
                                }}
                                variant="outlined"
                                className="calender-button"
                            >
                                {/* <RefreshOutlined /> */}
                                {t('report.reset')}
                            </Button>
                            <Button
                                onClick={() => {
                                    setExpanded(false);
                                    playButtonAudio();
                                }}
                                variant="outlined"
                                className="calender-button"
                            >
                                {/* <CloseOutlined /> */}
                                {t('report.ok')}
                            </Button>
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            </Stack>
            {/* <ReportDivider /> */}
        </>
    );
};
type PresetRangeButtonOwnProps = {
    Last: 0 | 1 | 7 | 14;
    cn: string;
    selected: number;
    setSelectedButton: Dispatch<SetStateAction<number>>;
    gaKey: string;
};
type PresetRangeButtonProps = DateRangePickerSetProps &
    PresetRangeButtonOwnProps;

const PresetRangeButton = (props: PresetRangeButtonProps) => {
    const {
        Last,
        setStart,
        setEnd,
        setExpanded,
        handleSubmit,
        playButtonAudio,
        selected,
        setSelectedButton,
        cn,
        gaKey,
    } = props;

    const single = Last > 1;
    const { t } = useTranslation();
    const local_cn = useMemo(
        () => (Last == selected ? `${cn} selected` : cn),
        [cn, Last, selected]
    );
    console.log('????', local_cn, Last);
    // const sx = useMemo(() => {
    //     if (Last == selected) {
    //         return {
    //             direction: 'var(--text-direction)',
    //             borderColor: '#bf9a5d',
    //             boxShadow: '0 0 0 0.2rem rgba(191, 154, 93, 0.25)',
    //         } as SxProps<Theme>;
    //     } else {
    //         return {
    //             direction: 'var(--text-direction)',
    //         } as SxProps<Theme>;
    //     }
    // }, [Last, selected]);

    return (
        <Button
            id={gaKey}
            className={local_cn}
            variant="outlined"
            onClick={() => {
                const now = getSAToday();
                const last =
                    Last === 0
                        ? moment(now).subtract(3, 'months')
                        : moment(now).subtract(Last, 'day');
                setStart(last);
                setEnd(now);
                setExpanded(false);
                handleSubmit?.(last, now, Last === 0, Last <= 1);
                playButtonAudio();
                setSelectedButton(Last);
            }}
            // sx={sx}
        >
            {t(
                `betlog.${single ? `last${Last}days` : Last === 0 ? 'recent' : 'today'}`
            )}
        </Button>
    );
};

type DateRangeButtonProps = {
    time: DateRangeDate;
    label: string;
};
const DateRangeButton = (props: DateRangeButtonProps) => {
    const { time, label } = props;
    return (
        <Grid item container className="date-range-button">
            <Grid item className="date-range-button-date-text">
                {moment(time).locale('en').format('YYYY/MM/DD')}
            </Grid>
            <Grid item className="date-range-button-time-text">
                {label}
                <br />
                (UTC+8)
            </Grid>
        </Grid>
    );
};
